import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS } from '@ipfabric/utils/aim/flow-protocols';
import { useMemo } from 'react';
import { generateGuid } from 'utils/guid';
import { z } from 'zod';
import type { AimFlowDraft, AimWorkloadDraft } from '../../../model/aimDrafts';
import { AimEntityCombobox } from './AimEntityCombobox';

type ProtocolFormValue = keyof typeof AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS;

const requiredUuid = (fieldLabel: string) =>
  z
    .string({ required_error: `${fieldLabel} is required` })
    .nullable()
    .refine((v) => v !== null, { message: `${fieldLabel} is required` })
    .pipe(z.string().uuid(`${fieldLabel} is required`));

const optionalPortNumber = (fieldLabel: string) =>
  z.string().refine((val) => {
    if (val.trim() === '') {
      return true;
    }

    const num = Number(val);
    return Number.isInteger(num) && num >= 0 && num <= 65535;
  }, `${fieldLabel} must be a number between 0 and 65535`);

export const aimFlowFormSchema = z.object({
  srcWorkloadId: requiredUuid('Source workload'),
  dstWorkloadId: requiredUuid('Destination workload'),
  srcIp: z
    .string()
    .min(1, 'Source IP is required')
    .ip({ message: 'Source IP must be a valid IP address' }),
  dstIp: z
    .string()
    .min(1, 'Destination IP is required')
    .ip({ message: 'Destination IP must be a valid IP address' }),
  protocol: z.enum(
    Object.keys(AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS) as [
      ProtocolFormValue,
      ...ProtocolFormValue[],
    ],
    { required_error: 'Protocol is required' },
  ),
  srcPort: optionalPortNumber('Source port'),
  dstPort: optionalPortNumber('Destination port'),
});

type AimFlowFormInputValues = {
  srcWorkloadId: string | null;
  dstWorkloadId: string | null;
  srcIp: string;
  dstIp: string;
  srcPort: string;
  dstPort: string;
  protocol: string;
};

type AimFlowFormOutputValues = z.output<typeof aimFlowFormSchema>;

const DEFAULT_PROTOCOL: ProtocolFormValue = '1'; // ICMP

const numberToString = (port: number | null): string =>
  port === null ? '' : String(port);

const stringToNumber = (value: string): number | null =>
  value.trim() === '' ? null : Number(value);

export const toAimFlowFormValues = (
  row?: AimFlowDraft,
): AimFlowFormInputValues => ({
  srcWorkloadId: row?.srcWorkloadId ?? null,
  dstWorkloadId: row?.dstWorkloadId ?? null,
  srcIp: row?.srcIp ?? '',
  dstIp: row?.dstIp ?? '',
  protocol: row ? String(row.protocol) : DEFAULT_PROTOCOL,
  srcPort: numberToString(row?.srcPort ?? null),
  dstPort: numberToString(row?.dstPort ?? null),
});

type Props = {
  formType: 'add' | 'edit';
  initialValues?: AimFlowDraft;
  availableWorkloads: AimWorkloadDraft[];
  onClose: () => void;
  onSubmit: (entity: AimFlowDraft) => void;
};

export const AimFlowDialog = ({
  formType,
  initialValues,
  availableWorkloads,
  onClose,
  onSubmit,
}: Props) => {
  const form = useCustomForm<
    AimFlowFormInputValues,
    unknown,
    AimFlowFormOutputValues
  >({
    resolver: zodResolver(aimFlowFormSchema),
    defaultValues: toAimFlowFormValues(initialValues),
  });

  const workloadOptions = useMemo(
    () => availableWorkloads.map((w) => ({ value: w.id, label: w.name })),
    [availableWorkloads],
  );

  const handleSave = form.handleSubmit((values) => {
    const entity: AimFlowDraft = {
      id:
        formType === 'edit' && initialValues
          ? initialValues.id
          : generateGuid(),
      srcWorkloadId: values.srcWorkloadId,
      dstWorkloadId: values.dstWorkloadId,
      srcIp: values.srcIp,
      dstIp: values.dstIp,
      srcPort: stringToNumber(values.srcPort),
      dstPort: stringToNumber(values.dstPort),
      protocol: Number(values.protocol),
    };
    onSubmit(entity);
    onClose();
  });

  return (
    <SidePanel
      title={formType === 'add' ? 'Add manual flow' : 'Edit manual flow'}
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            disabled={!form.formState.isDirty}
            loading={form.formState.isSubmitting}
            onClick={handleSave}
            dataTestId='aim-flow-save'
          >
            {formType === 'add' ? 'Add manual flow' : 'Save changes'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form>
        <Card className='flex flex-col gap p-sm'>
          <AimEntityCombobox
            control={form.control}
            name='srcWorkloadId'
            label='Source workload'
            options={workloadOptions}
            emptyOptionsHint='Add a Workload first.'
          />
          <AimEntityCombobox
            control={form.control}
            name='dstWorkloadId'
            label='Destination workload'
            options={workloadOptions}
            emptyOptionsHint='Add a Workload first.'
          />
          <FormTextField
            type='text'
            control={form.control}
            name='srcIp'
            label='Source IP'
          />
          <FormTextField
            type='text'
            control={form.control}
            name='dstIp'
            label='Destination IP'
          />
          <FormSelect.Mandatory
            control={form.control}
            name='protocol'
            label='Protocol'
            options={Object.entries(AIM_MANUAL_FLOW_ALLOWED_PROTOCOLS).map(
              ([value, label]) => ({ value, label }),
            )}
          />
          <FormTextField
            type='number'
            control={form.control}
            name='srcPort'
            label='Source port (optional)'
            min={0}
            max={65535}
          />
          <FormTextField
            type='number'
            control={form.control}
            name='dstPort'
            label='Destination port (optional)'
            min={0}
            max={65535}
          />
        </Card>
      </form>
    </SidePanel>
  );
};
